import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { formatNumero, formatDateTime } from "../../utils/Utilitats";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import TeclaEscEnter from "../../utils/Teclaescenter";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

const GRUPS_UBICACIO = [
  ["magatzems", "Magatzems"],
  ["vehicles", "Vehicles"],
  ["obres", "Obres"],
  ["altres", "Altres"],
];

const TIPUS_MOVIMENT = {
  ENTRADA: "Entrada",
  TRASLLAT: "Trasllat",
  CONSUM: "Consum",
  REGULARITZACIO: "Regularització",
};

function textTipusMoviment(fila) {
  if (fila.tipus === "REGULARITZACIO" && fila.sentit === "positiva") return "Regularització positiva";
  if (fila.tipus === "REGULARITZACIO" && fila.sentit === "negativa") return "Regularització negativa";
  return TIPUS_MOVIMENT[fila.tipus] || fila.tipus;
}

function PanellEstocProducte({ product, idEmpresa, potTraslladar, onEstoc }) {
  const pack = Number(product?.PackComposicioActiva) === 1;
  const [situacio, setSituacio] = useState(null);
  const [error, setError] = useState("");
  const [formulari, setFormulari] = useState(null);
  const [ocupat, setOcupat] = useState(false);
  const [idOrigen, setIdOrigen] = useState("");
  const [idDesti, setIdDesti] = useState("");
  const [quantitat, setQuantitat] = useState("");
  const [observacions, setObservacions] = useState("");
  const [destinacions, setDestinacions] = useState(null);
  const [signe, setSigne] = useState("positiva");
  const [idUbicacioReg, setIdUbicacioReg] = useState("");
  const [quantitatReg, setQuantitatReg] = useState("");
  const [motiu, setMotiu] = useState("");

  const onEstocRef = useRef(onEstoc);
  onEstocRef.current = onEstoc;

  const carregar = useCallback(async () => {
    if (!product?.idProductes) return;
    setError("");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Estocproducte.php`, {
        method: "POST",
        body: JSON.stringify({ idProductes: product.idProductes, idEmpresa, incloureReserves: false }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'ha pogut consultar l'estoc.");
        setSituacio(null);
        return;
      }
      setSituacio(dades);
      if (dades.estoc) onEstocRef.current?.(dades.estoc.fisic, dades.estoc.reservat);
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      setError("No s'ha pogut consultar l'estoc.");
    }
  }, [product?.idProductes, idEmpresa]);

  useEffect(() => {
    setFormulari(null);
    carregar();
  }, [carregar]);

  const origen = (situacio?.origensTrasllat || []).find((fila) => String(fila.idUbicacions) === String(idOrigen));
  const maxim = Number(origen?.quantitat) || 0;
  const quantitatNumero = Number(String(quantitat).replace(",", "."));
  const previsualitzacio =
    pack && Number.isFinite(quantitatNumero) && quantitatNumero > 0
      ? (product?.componentsPack || []).map((component) => ({
          nom: [component.marca, component.model].filter(Boolean).join(" ") || "Component",
          quantitat: quantitatNumero * Number(component.quantitat),
        }))
      : [];

  const obrir = async () => {
    const primera = (situacio?.origensTrasllat || [])[0];
    setIdOrigen(primera ? String(primera.idUbicacions) : "");
    setQuantitat(primera ? String(primera.quantitat) : "");
    setIdDesti("");
    setObservacions("");
    setFormulari("trasllat");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Ubicacions.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa }),
      });
      const dades = await resposta.json();
      if (dades.success) setDestinacions(dades);
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
    }
  };

  const enviar = async (event) => {
    event.preventDefault();
    if (ocupat) return;
    if (!origen) {
      alert("Cal una ubicació d'origen.");
      return;
    }
    if (!Number.isFinite(quantitatNumero) || quantitatNumero <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    if (quantitatNumero > maxim + 0.001) {
      alert(`El màxim que es pot traslladar des d'aquí és ${formatNumero(maxim)}.`);
      return;
    }
    if (!idDesti || String(idDesti) === String(idOrigen)) {
      alert("Cal un destí diferent de l'origen.");
      return;
    }
    setOcupat(true);
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Traslladarproducte.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idProductes: product.idProductes,
          quantitat: quantitatNumero,
          idUbicacioOrigen: Number(idOrigen),
          idUbicacioDesti: Number(idDesti),
          observacions: observacions.trim() || null,
        }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        alert(dades.message || "No s'ha pogut fer el trasllat.");
        return;
      }
      setFormulari(null);
      await carregar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut fer el trasllat.");
    } finally {
      setOcupat(false);
    }
  };

  const obrirRegularitzacio = async () => {
    setSigne("positiva");
    setIdUbicacioReg("");
    setQuantitatReg("");
    setMotiu("");
    setFormulari("regularitzacio");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Ubicacions.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa }),
      });
      const dades = await resposta.json();
      if (dades.success) setDestinacions(dades);
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
    }
  };

  const enviarRegularitzacio = async (event) => {
    event.preventDefault();
    if (ocupat) return;
    const q = Number(String(quantitatReg).replace(",", "."));
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    if (!motiu.trim()) {
      alert("El motiu és obligatori.");
      return;
    }
    if (!idUbicacioReg) {
      alert("Cal una ubicació.");
      return;
    }
    const origenReg = origens.find((fila) => String(fila.idUbicacions) === String(idUbicacioReg));
    if (signe === "negativa" && q > (Number(origenReg?.quantitat) || 0) + 0.001) {
      alert(`El màxim que es pot treure d'aquí és ${formatNumero(origenReg?.quantitat || 0)}.`);
      return;
    }
    const fisic = Number(situacio?.estoc?.fisic) || 0;
    const reservat = Number(situacio?.estoc?.reservat) || 0;
    const nouDisponible = fisic + (signe === "negativa" ? -q : q) - reservat;
    if (nouDisponible < -0.001 && !window.confirm("Aquesta regularització deixarà l'estoc disponible en negatiu. Vols continuar?")) {
      return;
    }
    setOcupat(true);
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Regularitzarproducte.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idProductes: product.idProductes,
          quantitat: q,
          signe,
          idUbicacions: Number(idUbicacioReg),
          observacions: motiu.trim(),
        }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        alert(dades.message || "No s'ha pogut fer la regularització.");
        return;
      }
      setFormulari(null);
      await carregar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut fer la regularització.");
    } finally {
      setOcupat(false);
    }
  };

  const llocs = situacio?.distribucio || [];
  const origens = situacio?.origensTrasllat || [];
  const historial = situacio?.historial || [];
  const unitat = pack ? " pack" : "";
  const quantitatRegNumero = Number(String(quantitatReg).replace(",", "."));
  const saldoReg = (() => {
    const deOrigen = origens.find((fila) => String(fila.idUbicacions) === String(idUbicacioReg));
    if (deOrigen) return Number(deOrigen.quantitat) || 0;
    const deLloc = llocs.find((fila) => String(fila.idUbicacions) === String(idUbicacioReg));
    return deLloc ? Number(deLloc.quantitat) || 0 : 0;
  })();
  const fisicActual = Number(situacio?.estoc?.fisic) || 0;
  const nouFisic =
    Number.isFinite(quantitatRegNumero) && quantitatRegNumero > 0
      ? fisicActual + (signe === "negativa" ? -quantitatRegNumero : quantitatRegNumero)
      : null;
  const previsualitzacioReg =
    pack && Number.isFinite(quantitatRegNumero) && quantitatRegNumero > 0
      ? (product?.componentsPack || []).map((component) => ({
          nom: [component.marca, component.model].filter(Boolean).join(" ") || "Component",
          quantitat: quantitatRegNumero * Number(component.quantitat),
        }))
      : [];

  return (
    <section className="dv2-card">
      <div className="dv2-card-head">
        <span className="dv2-card-num">3</span>
        <h2 className="dv2-card-title">On és</h2>
        {potTraslladar && !formulari && (
          <div className="dv2-footerActions" style={{ marginLeft: "auto" }}>
            <button type="button" className="dv2-btn" onClick={obrirRegularitzacio}>
              Regularitzar estoc
            </button>
            {origens.length > 0 && (
              <button type="button" className="dv2-btn" onClick={obrir}>
                Traslladar
              </button>
            )}
          </div>
        )}
      </div>
      {error ? <p className="dv2-banner dv2-banner--danger">{error}</p> : null}
      {pack && situacio?.estoc?.ubicacio ? (
        <p className="dv2-hint">Ubicació del pack: {situacio.estoc.ubicacio}</p>
      ) : null}
      {origens.length === 0 && llocs.length === 0 ? (
        <p className="dv2-hint">No hi ha estoc ubicat per a aquesta empresa.</p>
      ) : (
        <ul>
          {(pack ? origens : llocs).map((fila) => (
            <li key={fila.idUbicacions}>
              {fila.nom}
              {fila.activa === false ? " (inactiva)" : ""} · {formatNumero(fila.quantitat)}
              {pack ? " pack" : ""}
              {fila.saldoNegatiu ? " · saldo negatiu" : ""}
            </li>
          ))}
        </ul>
      )}
      {pack && origens.length === 0 && situacio?.estoc?.ubicacio === "Distribuït" ? (
        <p className="dv2-hint">Els components no coincideixen en una sola ubicació, així que no es pot traslladar un pack sencer.</p>
      ) : null}
      {formulari === "trasllat" && (
        <form className="dv2-fields dv2-fields--sizes" onSubmit={enviar}>
          <label className="dv2-field dv2-field--llarg">
            <span className="dv2-label">Origen</span>
            <select
              className="dv2-select"
              value={idOrigen}
              onChange={(e) => {
                const id = e.target.value;
                setIdOrigen(id);
                const fila = origens.find((item) => String(item.idUbicacions) === id);
                setQuantitat(fila ? String(fila.quantitat) : "");
                if (String(idDesti) === id) setIdDesti("");
              }}
            >
              {origens.map((fila) => (
                <option key={fila.idUbicacions} value={fila.idUbicacions}>
                  {fila.nom}
                  {fila.activa === false ? " (inactiva)" : ""} · {formatNumero(fila.quantitat)}
                  {unitat}
                </option>
              ))}
            </select>
          </label>
          <label className="dv2-field dv2-field--llarg">
            <span className="dv2-label">Destí</span>
            <select className="dv2-select" value={idDesti} onChange={(e) => setIdDesti(e.target.value)}>
              <option value="">Tria una ubicació</option>
              {GRUPS_UBICACIO.map(([clau, etiqueta]) => {
                const items = (destinacions?.[clau] || []).filter((item) => String(item.idUbicacions) !== String(idOrigen));
                if (!items.length) return null;
                return (
                  <optgroup key={clau} label={etiqueta}>
                    {items.map((item) => (
                      <option key={item.idUbicacions} value={item.idUbicacions}>
                        {item.nom}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </label>
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Quantitat</span>
            <input
              className="dv2-input"
              type="number"
              min="0.01"
              step="0.01"
              max={maxim || undefined}
              value={quantitat}
              onChange={(e) => setQuantitat(e.target.value)}
            />
            <span className="dv2-hint">Màxim a l'origen: {formatNumero(maxim)}{unitat}</span>
          </label>
          <label className="dv2-field dv2-field--ample">
            <span className="dv2-label">Observacions</span>
            <input
              className="dv2-input"
              type="text"
              maxLength={255}
              value={observacions}
              onChange={(e) => setObservacions(e.target.value)}
            />
          </label>
          {previsualitzacio.length > 0 && (
            <div className="dv2-field dv2-field--ample">
              <span className="dv2-hint">
                {formatNumero(quantitatNumero)} packs equivalen a:{" "}
                {previsualitzacio.map((component) => `${formatNumero(component.quantitat)} × ${component.nom}`).join(", ")}
              </span>
            </div>
          )}
          <div className="dv2-field dv2-field--ample">
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" disabled={ocupat} onClick={() => setFormulari(null)}>
                Tancar
              </button>
              <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
                Traslladar
              </button>
            </div>
          </div>
        </form>
      )}
      {formulari === "regularitzacio" && (
        <form className="dv2-fields dv2-fields--sizes" onSubmit={enviarRegularitzacio}>
          <div className="dv2-field dv2-field--mig">
            <span className="dv2-label">Tipus</span>
            <label>
              <input
                type="radio"
                name="signe-regularitzacio"
                checked={signe === "positiva"}
                onChange={() => {
                  setSigne("positiva");
                  setIdUbicacioReg("");
                }}
              />{" "}
              Positiva
            </label>
            <label>
              <input
                type="radio"
                name="signe-regularitzacio"
                checked={signe === "negativa"}
                onChange={() => {
                  setSigne("negativa");
                  setIdUbicacioReg("");
                }}
              />{" "}
              Negativa
            </label>
          </div>
          <label className="dv2-field dv2-field--llarg">
            <span className="dv2-label">Ubicació</span>
            <select className="dv2-select" value={idUbicacioReg} onChange={(e) => setIdUbicacioReg(e.target.value)}>
              <option value="">Tria una ubicació</option>
              {signe === "negativa"
                ? origens.map((fila) => (
                    <option key={fila.idUbicacions} value={fila.idUbicacions}>
                      {fila.nom}
                      {fila.activa === false ? " (inactiva)" : ""} · {formatNumero(fila.quantitat)}
                      {unitat}
                    </option>
                  ))
                : GRUPS_UBICACIO.map(([clau, etiqueta]) => {
                    const items = destinacions?.[clau] || [];
                    if (!items.length) return null;
                    return (
                      <optgroup key={clau} label={etiqueta}>
                        {items.map((item) => (
                          <option key={item.idUbicacions} value={item.idUbicacions}>
                            {item.nom}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
            </select>
            {signe === "negativa" && origens.length === 0 ? (
              <span className="dv2-hint">No hi ha saldo per fer una sortida.</span>
            ) : null}
          </label>
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Quantitat</span>
            <input
              className="dv2-input"
              type="number"
              min="0.01"
              step="0.01"
              value={quantitatReg}
              onChange={(e) => setQuantitatReg(e.target.value)}
            />
          </label>
          <label className="dv2-field dv2-field--ample">
            <span className="dv2-label dv2-required">Motiu</span>
            <input
              className="dv2-input"
              type="text"
              maxLength={255}
              value={motiu}
              onChange={(e) => setMotiu(e.target.value)}
            />
          </label>
          {idUbicacioReg && nouFisic !== null && (
            <div className="dv2-field dv2-field--ample">
              <span className="dv2-hint">
                Estoc actual: {formatNumero(fisicActual)}
                {unitat}. Saldo a la ubicació: {formatNumero(saldoReg)}
                {unitat}. Nou estoc físic: {formatNumero(nouFisic)}
                {unitat}.
              </span>
            </div>
          )}
          {previsualitzacioReg.length > 0 && (
            <div className="dv2-field dv2-field--ample">
              <span className="dv2-hint">
                {formatNumero(quantitatRegNumero)} packs equivalen a:{" "}
                {previsualitzacioReg.map((component) => `${formatNumero(component.quantitat)} × ${component.nom}`).join(", ")}
              </span>
            </div>
          )}
          <div className="dv2-field dv2-field--ample">
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" disabled={ocupat} onClick={() => setFormulari(null)}>
                Tancar
              </button>
              <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
                Regularitzar
              </button>
            </div>
          </div>
        </form>
      )}
      {historial.length > 0 && (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipus</th>
                <th className="dv2-num">Quantitat</th>
                <th>Origen</th>
                <th>Destí</th>
                <th>Usuari</th>
                <th>Observacions</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((fila) => (
                <tr key={`${fila.id}-${fila.unitat || "u"}-${fila.tipus}`}>
                  <td>{formatDateTime(String(fila.data || "").replace(" ", "T"))}</td>
                  <td>{textTipusMoviment(fila)}</td>
                  <td className="dv2-num">
                    {formatNumero(fila.quantitat)}
                    {fila.unitat === "pack" ? " pack" : ""}
                  </td>
                  <td>{fila.origen || "—"}</td>
                  <td>{fila.desti || "—"}</td>
                  <td>{fila.usuari || "—"}</td>
                  <td>{fila.observacions || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Detallsproducte.js
 * Millora UX: creació inline (modal) de Categoria → Tipus de material → Material
 * - Sense sortir del flux de Nou/Modificar Producte
 * - Després de crear, refresca llistes i deixa els selects seleccionats correctament (per ID)
 */
function Detallsproducte({ mode, product, productes, onSave, onCancel, onNavigate, onCanviarMode, onDeleted, subnav }) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";

  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrarFitxa = tincPermis(rolUsuari, "ESBORRAR");

  // ======= HELPERS =======
  const normalize = useCallback((s) => (s ?? "").toString().trim(), []);
  const collator = useMemo(() => new Intl.Collator("ca", { sensitivity: "base" }), []);

  // ======= ESTATS PRODUCTE =======
  const [marca, setMarca] = useState("");
  const [model, setModel] = useState("");
  const [refProducte, setRefProducte] = useState("");
  const [descripcio, setDescripcio] = useState("");
  const [estocFisic, setEstocFisic] = useState(0);
  const [estocReservat, setEstocReservat] = useState(0);
  const [unitatMesura, setUnitatMesura] = useState("ut");
  const [unitatOptions, setUnitatOptions] = useState([]);
  const [qualitat, setQualitat] = useState("Mitjana");
  const [qualitatOptions, setQualitatOptions] = useState([]);
  const [espack, setEsPack] = useState(0);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  // Imatge
  const [imatge, setImatge] = useState("");
  const [imatgeFile, setImatgeFile] = useState(null);
  const fileInputRef = useRef(null);

  // ======= SELECTS (IDs) =======
  const [categoriesOptions, setCategoriesOptions] = useState([]); // [{idCategories, Categoria}]
  const [tipusOptions, setTipusOptions] = useState([]); // [{idTipusDeMaterials, Tipus, Categories_idCategories}]
  const [materialsOptions, setMaterialsOptions] = useState([]); // Materials.php (inclou idMaterials, idCategories, idTipusDeMaterials, ...)
  const [selectedCategoriaId, setSelectedCategoriaId] = useState(null);
  const [selectedTipusId, setSelectedTipusId] = useState(null);
  const [idMaterials, setIdMaterials] = useState(null);

  // Info del material seleccionat (per mostrar el “resum”)
  const [materialInfo, setMaterialInfo] = useState({
    Categoria: "",
    Tipus: "",
    NomGeneric: "",
    Descripcio: "",
  });

  // Navegació
  const currentIndex = productes ? productes.findIndex((p) => p.idProductes === product?.idProductes) : -1;

  const anteriorProducte = () => {
    if (!productes || productes.length === 0) return;
    const newIndex = currentIndex === 0 ? productes.length - 1 : currentIndex - 1;
    onNavigate(productes[newIndex]);
  };

  const seguentProducte = () => {
    if (!productes || productes.length === 0) return;
    const newIndex = currentIndex === productes.length - 1 ? 0 : currentIndex + 1;
    onNavigate(productes[newIndex]);
  };

  const resetForm = useCallback(() => {
    setMarca("");
    setModel("");
    setRefProducte("");
    setDescripcio("");
    setEstocFisic(0);
    setEstocReservat(0);
    setUnitatMesura("ut");
    setQualitat("Mitjana");
    setEsPack(0);

    setSelectedCategoriaId(null);
    setSelectedTipusId(null);
    setIdMaterials(null);
    setMaterialInfo({ Categoria: "", Tipus: "", NomGeneric: "", Descripcio: "" });

    setImatge("");
    setImatgeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  }, []);

  // ========= FETCHERS =========
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/Categories.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa || 0 }),
      });
      const data = await response.json();
      if (data.success) {
        const list = (data.categories || []).slice().sort((a, b) =>
          collator.compare(normalize(a.Categoria), normalize(b.Categoria))
        );
        setCategoriesOptions(list);
        return list;
      }
      console.error("Error carregant categories:", data.message);
      setCategoriesOptions([]);
      return [];
    } catch (error) {
      console.error("Error al fetchCategories:", error);
      setCategoriesOptions([]);
      return [];
    }
  }, [empresaSeleccionada, collator, normalize]);

  const fetchTipus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/Tipusdematerials.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mostrarActius: 1 }),
      });
      const data = await response.json();
      if (data.success) {
        const list = (data.tipusDeMaterials || []).slice().sort((a, b) =>
          collator.compare(normalize(a.Tipus), normalize(b.Tipus))
        );
        setTipusOptions(list);
        return list;
      }
      console.error("Error carregant tipus:", data.message);
      setTipusOptions([]);
      return [];
    } catch (error) {
      console.error("Error al fetchTipus:", error);
      setTipusOptions([]);
      return [];
    }
  }, [collator, normalize]);

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/Materials.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mostrarActius: 1 }),
      });
      const data = await response.json();
      if (data.success) {
        const list = data.materials || [];
        setMaterialsOptions(list);
        return list;
      }
      console.error("Error carregant materials:", data.message);
      setMaterialsOptions([]);
      return [];
    } catch (error) {
      console.error("Error al fetchMaterials:", error);
      setMaterialsOptions([]);
      return [];
    }
  }, []);

  // ========= LOAD LISTES =========
  useEffect(() => {
    fetchCategories();
    fetchTipus();
    fetchMaterials();
  }, [fetchCategories, fetchTipus, fetchMaterials]);

  // Enums
  useEffect(() => {
    const fetchUnitatsEnum = async () => {
      try {
        const resp = await fetch(`${API_URL}/Unitatsmesura.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json();
        if (data.success) setUnitatOptions(data.enumValues);
        else setUnitatOptions(["ut", "m", "m2"]);
      } catch (error) {
        console.error("Error al fetchUnitatsEnum:", error);
        setUnitatOptions(["ut", "m", "m2"]);
      }
    };
    fetchUnitatsEnum();
  }, []);

  useEffect(() => {
    const fetchQualitatEnum = async () => {
      try {
        const resp = await fetch(`${API_URL}/Qualitatproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json();
        if (data.success) setQualitatOptions(data.enumValues);
        else setQualitatOptions(["Baixa", "Mitjana", "Alta"]);
      } catch (error) {
        console.error("Error al fetchQualitatEnum:", error);
        setQualitatOptions(["Baixa", "Mitjana", "Alta"]);
      }
    };
    fetchQualitatEnum();
  }, []);

  // ========= LOAD PRODUCTE =========
  useEffect(() => {
    if (product) {
      setMarca(product.Marca || "");
      setModel(product.Model || "");
      setRefProducte(product.RefProducte || "");
      setDescripcio(product.Descripcio || "");
      setEstocFisic(product.EstocFisic || 0);
      setEstocReservat(product.EstocReservat || 0);
      setUnitatMesura(product.UnitatMesura || "ut");
      setQualitat(product.Qualitat || "Mitjana");
      setEsPack(product.EsPack || 0);

      setIdMaterials(product.Materials_idMaterials || null);

      setImatge(product.Imatge ? `${API_URL}/imatgesprod/${product.Imatge}` : "");
      setImatgeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    } else {
      resetForm();
    }
  }, [product, mode, resetForm]);

  // ========= MATERIAL SELECCIONAT → INFO + IDS PARE =========
  useEffect(() => {
    if (!idMaterials) {
      setMaterialInfo({ Categoria: "", Tipus: "", NomGeneric: "", Descripcio: "" });
      return;
    }

    const found = (materialsOptions || []).find((m) => Number(m.idMaterials) === Number(idMaterials));
    if (!found) {
      setMaterialInfo({ Categoria: "", Tipus: "", NomGeneric: "", Descripcio: "" });
      return;
    }

    setMaterialInfo({
      Categoria: found.Categoria || "",
      Tipus: found.Tipus || "",
      NomGeneric: found.NomGeneric || "",
      Descripcio: found.Descripcio || "",
    });

    // IDs (ja venen explícits de Materials.php)
    const tipusId =
      found.idTipusDeMaterials != null
        ? Number(found.idTipusDeMaterials)
        : found.TipusDeMaterials_idTipusDeMaterials != null
        ? Number(found.TipusDeMaterials_idTipusDeMaterials)
        : null;

    let catId =
      found.idCategories != null
        ? Number(found.idCategories)
        : found.Categories_idCategories != null
        ? Number(found.Categories_idCategories)
        : null;

    if (catId == null && tipusId != null) {
      const tipusPare = (tipusOptions || []).find(
        (t) => Number(t.idTipusDeMaterials) === Number(tipusId)
      );
      if (tipusPare?.Categories_idCategories != null) {
        catId = Number(tipusPare.Categories_idCategories);
      }
    }

    if (catId) setSelectedCategoriaId(catId);
    if (tipusId) setSelectedTipusId(tipusId);
  }, [idMaterials, materialsOptions, tipusOptions]);

  // ========= Llistes filtrades =========
  const tipusList = useMemo(() => {
    const list = (tipusOptions || [])
      .filter((t) => !selectedCategoriaId || Number(t.Categories_idCategories) === Number(selectedCategoriaId))
      .slice()
      .sort((a, b) => collator.compare(normalize(a.Tipus), normalize(b.Tipus)));
    return list;
  }, [tipusOptions, selectedCategoriaId, collator, normalize]);

  const materialsList = useMemo(() => {
    const list = (materialsOptions || [])
      .filter((m) => {
        const tipusId =
          m.idTipusDeMaterials != null
            ? Number(m.idTipusDeMaterials)
            : m.TipusDeMaterials_idTipusDeMaterials != null
            ? Number(m.TipusDeMaterials_idTipusDeMaterials)
            : null;

        if (selectedTipusId && tipusId !== Number(selectedTipusId)) return false;

        // Si no hi ha tipus seleccionat però sí categoria, filtrem per categoria
        if (!selectedTipusId && selectedCategoriaId) {
          const catId = m.idCategories != null ? Number(m.idCategories) : null;
          if (catId !== Number(selectedCategoriaId)) return false;
        }

        return true;
      })
      .slice()
      .sort((a, b) => collator.compare(normalize(a.NomGeneric), normalize(b.NomGeneric)));

    return list;
  }, [materialsOptions, selectedCategoriaId, selectedTipusId, collator, normalize]);

  // ========= CANVIS DE SELECT =========
  const handleChangeCategoriaId = (value) => {
    const id = value ? Number(value) : null;
    setSelectedCategoriaId(id);
    setSelectedTipusId(null);
    setIdMaterials(null);
    setMaterialInfo({ Categoria: "", Tipus: "", NomGeneric: "", Descripcio: "" });
  };

  const handleChangeTipusId = (value) => {
    if (value === "__new__") {
      if (!pucCrear) return;
      if (!selectedCategoriaId) {
        alert("Primer has de seleccionar una Categoria.");
        return;
      }
      openModal("tipus");
      return;
    }

    const id = value ? Number(value) : null;
    setSelectedTipusId(id);
    setIdMaterials(null);
    setMaterialInfo({ Categoria: "", Tipus: "", NomGeneric: "", Descripcio: "" });
  };

  const handleChangeMaterialId = (value) => {
    if (value === "__new__") {
      if (!pucCrear) return;
      if (!selectedTipusId) {
        alert("Primer has de seleccionar un Tipus.");
        return;
      }
      openModal("material");
      return;
    }
    setIdMaterials(value ? Number(value) : null);
  };

  // ========= VALIDACIÓ =========
  const validarFormulari = () => {
    const errors = [];
    if (!marca.trim()) errors.push("El camp 'Marca' és obligatori.");
    if (!model.trim()) errors.push("El camp 'Model' és obligatori.");
    if (!idMaterials) errors.push("Has de seleccionar un 'Material'.");
    return errors;
  };

  // ========= SUBMIT PRODUCTE =========
  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errors = validarFormulari();
      if (errors.length > 0) {
        alert("Errors trobats:\n" + errors.join("\n"));
        return;
      }
    }

    let nomImatge = imatgeFile ? null : product?.Imatge || "";

    if (imatgeFile) {
      const formData = new FormData();
      formData.append("imatge", imatgeFile);
      try {
        const uploadResponse = await fetch(`${API_URL}/Imatgeproducte.php`, {
          method: "POST",
          body: formData,
        });
        const uploadData = await uploadResponse.json();
        if (uploadData.success) {
          nomImatge = uploadData.imatge;
        } else {
          alert("Error en pujar la imatge: " + uploadData.message);
          return;
        }
      } catch (error) {
        console.error("Error en pujar la imatge:", error);
        alert("Error en pujar la imatge.");
        return;
      }
    }

    const productData = {
      idProductes: product?.idProductes || null,
      Marca: marca.trim(),
      Model: model.trim(),
      Materials_idMaterials: idMaterials,
      Descripcio: descripcio,
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      EstocFisic: estocFisic,
      UnitatMesura: unitatMesura,
      Qualitat: qualitat,
      EsPack: espack,
      RefProducte: refProducte,
      Imatge: nomImatge,
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Nouproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productData),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.({ ...productData, idProductes: data.idProductes });
        } else {
          alert("Error en crear el producte: " + data.message);
        }
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificarproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productData),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.({ ...product, ...productData });
        } else {
          alert("Error en modificar el producte: " + data.message);
        }
      } else if (mode === "esborrar") {
        const resp = await fetch(`${API_URL}/Esborrarproducte.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idProductes: product.idProductes,
            idEmpresa: empresaSeleccionada?.IdEmpresa || null,
          }),
        });
        const data = await resp.json();
        if (data.success) {
          onDeleted?.();
        } else {
          alert("Error en esborrar el producte: " + data.message);
        }
      }
    } catch (error) {
      console.error("Error en handleSubmit Producte:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  // ========= IMATGE =========
  const MAX_WIDTH = 300;
  const MAX_HEIGHT = 300;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        setImatge(canvas.toDataURL("image/jpeg"));

        const newFileName = file.name.replace(/\.[^/.]+$/, ".jpg");
        canvas.toBlob(
          (blob) => {
            setImatgeFile(new File([blob], newFileName, { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.9
        );
      };
    };
  };

  // ===========================
  // MODALS INLINE (Categoria / Tipus / Material)
  // ===========================
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(null); // "categoria" | "tipus" | "material"
  const [modalBusy, setModalBusy] = useState(false);

  // Camps modal
  const [novaCategoriaNom, setNovaCategoriaNom] = useState("");
  const [nouTipusNom, setNouTipusNom] = useState("");
  const [nouMaterialNomGeneric, setNouMaterialNomGeneric] = useState("");
  const [nouMaterialDescripcio, setNouMaterialDescripcio] = useState("");
  const [nouMaterialUnitat, setNouMaterialUnitat] = useState("ut");
  const [nouMaterialActiu, setNouMaterialActiu] = useState(true);
  const [nouMaterialDataAlta, setNouMaterialDataAlta] = useState("");

  const avuiISO = useMemo(() => new Date().toISOString().split("T")[0], []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalStep(null);
    setModalBusy(false);

    setNovaCategoriaNom("");
    setNouTipusNom("");
    setNouMaterialNomGeneric("");
    setNouMaterialDescripcio("");
    setNouMaterialUnitat("ut");
    setNouMaterialActiu(true);
    setNouMaterialDataAlta("");
  }, []);

  TeclaEscEnter(modalOpen ? closeModal : onCancel, null);

  const openModal = (step) => {
    if (!pucCrear) return;
    setModalStep(step);
    setModalOpen(true);
    setModalBusy(false);
    if (step === "material") {
      setNouMaterialUnitat("ut");
      setNouMaterialActiu(true);
      setNouMaterialDataAlta(avuiISO);
    }
  };

  const createCategoria = async () => {
    const nom = normalize(novaCategoriaNom);
    if (!nom) {
      alert("El camp 'Categoria' no pot estar buit.");
      return null;
    }
    if (nom.length > 45) {
      alert("El nom de la categoria no pot superar 45 caràcters.");
      return null;
    }

    const duplicat = (categoriesOptions || []).find(
      (c) => normalize(c.Categoria).toLowerCase() === nom.toLowerCase()
    );
    if (duplicat) {
      alert("Ja existeix una categoria amb aquest nom.");
      setSelectedCategoriaId(Number(duplicat.idCategories));
      setSelectedTipusId(null);
      setIdMaterials(null);
      return null;
    }

    setModalBusy(true);
    try {
      const response = await fetch(`${API_URL}/Novacategoria.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: empresaSeleccionada?.IdEmpresa,
          Categoria: nom,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        alert(`Error creant Categoria: ${data.message}`);
        setModalBusy(false);
        return null;
      }

      const list = await fetchCategories();
      let idCategoria = data.idCategoria ? Number(data.idCategoria) : null;
      if (!idCategoria && Array.isArray(list)) {
        const found = list.find((c) => normalize(c.Categoria).toLowerCase() === nom.toLowerCase());
        idCategoria = found ? Number(found.idCategories) : null;
      }

      if (idCategoria) {
        setSelectedCategoriaId(idCategoria);
        setSelectedTipusId(null);
        setIdMaterials(null);
      }

      setModalBusy(false);
      return idCategoria;
    } catch (error) {
      console.error("Error createCategoria:", error);
      alert("S'ha produït un error en crear la categoria.");
      setModalBusy(false);
      return null;
    }
  };

  const createTipus = async () => {
    const nom = normalize(nouTipusNom);
    if (!selectedCategoriaId) {
      alert("Has de tenir una Categoria seleccionada per crear un Tipus.");
      return null;
    }
    if (!nom) {
      alert("El camp 'Tipus' no pot estar buit.");
      return null;
    }
    if (nom.length > 100) {
      alert("El nom del tipus no pot superar 100 caràcters.");
      return null;
    }

    const duplicat = (tipusOptions || []).find(
      (t) =>
        Number(t.Categories_idCategories) === Number(selectedCategoriaId) &&
        normalize(t.Tipus).toLowerCase() === nom.toLowerCase()
    );
    if (duplicat) {
      alert("Ja existeix aquest tipus dins de la categoria seleccionada.");
      setSelectedTipusId(Number(duplicat.idTipusDeMaterials));
      setIdMaterials(null);
      return null;
    }

    setModalBusy(true);
    try {
      const response = await fetch(`${API_URL}/Noutipusdematerial.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa: empresaSeleccionada?.IdEmpresa,
          Tipus: nom,
          Categories_idCategories: selectedCategoriaId,
          Actiu: 1,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        alert(`Error creant Tipus de Material: ${data.message}`);
        setModalBusy(false);
        return null;
      }

      const list = await fetchTipus();
      let idTipus = data.idTipusDeMaterial ? Number(data.idTipusDeMaterial) : null;
      if (!idTipus && Array.isArray(list)) {
        const found = list.find(
          (t) =>
            Number(t.Categories_idCategories) === Number(selectedCategoriaId) &&
            normalize(t.Tipus).toLowerCase() === nom.toLowerCase()
        );
        idTipus = found ? Number(found.idTipusDeMaterials) : null;
      }

      if (idTipus) {
        setSelectedTipusId(idTipus);
        setIdMaterials(null);
      }

      setModalBusy(false);
      return idTipus;
    } catch (error) {
      console.error("Error createTipus:", error);
      alert("S'ha produït un error en crear el tipus.");
      setModalBusy(false);
      return null;
    }
  };

  const createMaterial = async () => {
    const nom = normalize(nouMaterialNomGeneric);
    const desc = normalize(nouMaterialDescripcio);

    if (!selectedTipusId) {
      alert("Has de tenir un Tipus seleccionat per crear un Material.");
      return null;
    }
    if (!nom) {
      alert("El camp 'Nom Genèric' és obligatori.");
      return null;
    }
    if (nom.length > 45) {
      alert("El nom genèric no pot superar 45 caràcters.");
      return null;
    }

    const duplicat = (materialsOptions || []).find((m) => {
      const tipusId =
        m.idTipusDeMaterials != null
          ? Number(m.idTipusDeMaterials)
          : m.TipusDeMaterials_idTipusDeMaterials != null
          ? Number(m.TipusDeMaterials_idTipusDeMaterials)
          : null;
      return tipusId === Number(selectedTipusId) && normalize(m.NomGeneric).toLowerCase() === nom.toLowerCase();
    });
    if (duplicat) {
      alert("Ja existeix un material amb aquest nom genèric dins del mateix tipus.");
      setIdMaterials(Number(duplicat.idMaterials));
      return null;
    }

    setModalBusy(true);
    try {
      const payload = {
        idMaterials: null,
        TipusDeMaterials_idTipusDeMaterials: selectedTipusId,
        NomGeneric: nom,
        Descripcio: desc,
        UnitatsDisponibles: 0,
        UnitatMesura: nouMaterialUnitat || "ut",
        Actiu: nouMaterialActiu ? 1 : 0,
        DataAlta: nouMaterialDataAlta || avuiISO,
        DataBaixa: null,
        Empresa_idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      };

      const resp = await fetch(`${API_URL}/Noumaterial.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();
      if (!data.success) {
        alert("Error en crear el material: " + data.message);
        setModalBusy(false);
        return null;
      }

      const list = await fetchMaterials();
      let idMat = data.idMaterial ? Number(data.idMaterial) : null;
      if (!idMat && Array.isArray(list)) {
        const found = list.find((m) => {
          const tipusId =
            m.idTipusDeMaterials != null
              ? Number(m.idTipusDeMaterials)
              : m.TipusDeMaterials_idTipusDeMaterials != null
              ? Number(m.TipusDeMaterials_idTipusDeMaterials)
              : null;
          return tipusId === Number(selectedTipusId) && normalize(m.NomGeneric).toLowerCase() === nom.toLowerCase();
        });
        idMat = found ? Number(found.idMaterials) : null;
      }
      if (idMat) setIdMaterials(idMat);

      setModalBusy(false);
      return idMat;
    } catch (error) {
      console.error("Error createMaterial:", error);
      alert("S'ha produït un error en crear el material.");
      setModalBusy(false);
      return null;
    }
  };

  const renderModal = () => {
    if (!modalOpen) return null;

    const headerTitle =
      modalStep === "categoria" ? "Nova Categoria" : modalStep === "tipus" ? "Nou Tipus de Material" : "Nou Material";

    const subtitle =
      modalStep === "categoria"
        ? "Crea la categoria i, si vols, continua amb el Tipus i el Material."
        : modalStep === "tipus"
        ? "Crea el tipus per la categoria seleccionada i, si vols, continua amb el Material."
        : "Crea el material i el deixarem seleccionat al Producte.";

    const onPrimary = async () => {
      if (modalBusy) return;

      if (modalStep === "categoria") {
        const idCat = await createCategoria();
        if (idCat) closeModal();
      } else if (modalStep === "tipus") {
        const idTip = await createTipus();
        if (idTip) closeModal();
      } else if (modalStep === "material") {
        const idMat = await createMaterial();
        if (idMat) closeModal();
      }
    };

    const onContinue = async () => {
      if (modalBusy) return;

      if (modalStep === "categoria") {
        const idCat = await createCategoria();
        if (!idCat) return;

        setNouTipusNom("");
        setNouMaterialNomGeneric("");
        setNouMaterialDescripcio("");
        setNouMaterialUnitat("ut");
        setNouMaterialActiu(true);
        setNouMaterialDataAlta(avuiISO);
        setModalStep("tipus");
      } else if (modalStep === "tipus") {
        const idTip = await createTipus();
        if (!idTip) return;

        setNouMaterialNomGeneric("");
        setNouMaterialDescripcio("");
        setNouMaterialUnitat("ut");
        setNouMaterialActiu(true);
        setNouMaterialDataAlta(avuiISO);
        setModalStep("material");
      }
    };

    const categoriaNom = (() => {
      const c = (categoriesOptions || []).find((x) => Number(x.idCategories) === Number(selectedCategoriaId));
      return c ? c.Categoria : "";
    })();

    const tipusNom = (() => {
      const t = (tipusOptions || []).find((x) => Number(x.idTipusDeMaterials) === Number(selectedTipusId));
      return t ? t.Tipus : "";
    })();


    return (
      <div
        className="dv2-modalOverlay"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.target === e.currentTarget && closeModal()}
      >
        <div className="dv2-modal dv2-modal--wide">
          <h3 className="dv2-modal-title">{headerTitle}</h3>
          <p className="dv2-modal-lead">{subtitle}</p>
          <div className="dv2-steps">
            <span className={modalStep === "categoria" ? "is-active" : ""}>1. Categoria</span>
            <span className={modalStep === "tipus" ? "is-active" : ""}>2. Tipus</span>
            <span className={modalStep === "material" ? "is-active" : ""}>3. Material</span>
          </div>

            {modalStep === "categoria" && (
              <div className="dv2-fields dv2-fields--sizes">
                <label className="dv2-field dv2-field--ample">
                  <span className="dv2-label dv2-required">Categoria</span>
                  <input
                    type="text"
                    className="dv2-input"
                    value={novaCategoriaNom}
                    onChange={(e) => setNovaCategoriaNom(e.target.value)}
                    disabled={modalBusy}
                    maxLength={45}
                    autoFocus
                  />
                </label>
              </div>
            )}

            {modalStep === "tipus" && (
              <div className="dv2-fields dv2-fields--sizes">
                <label className="dv2-field dv2-field--llarg">
                  <span className="dv2-label">Categoria</span>
                  <span className="dv2-value">{categoriaNom || "—"}</span>
                </label>
                <label className="dv2-field dv2-field--llarg">
                  <span className="dv2-label dv2-required">Tipus</span>
                  <input
                    type="text"
                    className="dv2-input"
                    value={nouTipusNom}
                    onChange={(e) => setNouTipusNom(e.target.value)}
                    disabled={modalBusy}
                    maxLength={100}
                    autoFocus
                  />
                </label>
              </div>
            )}

            {modalStep === "material" && (
              <div className="dv2-fields dv2-fields--sizes">
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Categoria</span>
                  <span className="dv2-value">{categoriaNom || "—"}</span>
                </label>
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Tipus</span>
                  <span className="dv2-value">{tipusNom || "—"}</span>
                </label>
                <label className="dv2-field dv2-field--llarg">
                  <span className="dv2-label dv2-required">Nom genèric</span>
                  <input
                    type="text"
                    className="dv2-input"
                    value={nouMaterialNomGeneric}
                    onChange={(e) => setNouMaterialNomGeneric(e.target.value)}
                    disabled={modalBusy}
                    maxLength={45}
                    autoFocus
                  />
                </label>
                <label className="dv2-field dv2-field--ample">
                  <span className="dv2-label">Descripció</span>
                  <input
                    type="text"
                    className="dv2-input"
                    value={nouMaterialDescripcio}
                    onChange={(e) => setNouMaterialDescripcio(e.target.value)}
                    disabled={modalBusy}
                    maxLength={100}
                  />
                </label>
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Unitat de mesura</span>
                  <select
                    className="dv2-select"
                    value={nouMaterialUnitat}
                    onChange={(e) => setNouMaterialUnitat(e.target.value)}
                    disabled={modalBusy}
                  >
                    {unitatOptions.map((um) => (
                      <option key={um} value={um}>
                        {um}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dv2-field dv2-field--mig">
                  <span className="dv2-label">Data d'alta</span>
                  <input
                    type="date"
                    className="dv2-input"
                    value={nouMaterialDataAlta}
                    onChange={(e) => setNouMaterialDataAlta(e.target.value)}
                    disabled={modalBusy}
                  />
                </label>
                <label className="dv2-field dv2-field--curt">
                  <span className="dv2-label">Actiu</span>
                  <span className="dv2-toggleRow">
                    <span className="dv2-toggle">
                      <input
                        type="checkbox"
                        checked={nouMaterialActiu}
                        onChange={(e) => setNouMaterialActiu(e.target.checked)}
                        disabled={modalBusy}
                      />
                      <span className="dv2-toggle-track" />
                    </span>
                    {nouMaterialActiu ? "Sí" : "No"}
                  </span>
                </label>
              </div>
            )}

          <div className="dv2-modal-actions">
            <button type="button" className="dv2-btn" onClick={closeModal} disabled={modalBusy}>
              Tancar
            </button>
            {modalStep !== "material" && (
              <button type="button" className="dv2-btn" onClick={onContinue} disabled={modalBusy}>
                Crear i continuar
              </button>
            )}
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={onPrimary} disabled={modalBusy}>
              {modalStep === "material" ? "Crear i tancar" : "Crear"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample" ? "dv2-field--ample" : mida === "llarg" ? "dv2-field--llarg" : mida === "curt" ? "dv2-field--curt" : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const renderReadOnly = () => (
    <>
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Classificació</h2>
        </div>
        <p className="dv2-path">
          <strong>{materialInfo?.Categoria || "—"}</strong>
          <span> › </span>
          <strong>{materialInfo?.Tipus || "—"}</strong>
          <span> › </span>
          <strong>{materialInfo?.NomGeneric || "—"}</strong>
        </p>
        <div className="dv2-fields dv2-fields--sizes">
          {camp("Descripció del material", false, <span className="dv2-value">{materialInfo?.Descripcio || "—"}</span>, "ample")}
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Dades del producte</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          {camp("Marca", false, <span className="dv2-value">{marca || "—"}</span>, "llarg")}
          {camp("Model", false, <span className="dv2-value">{model || "—"}</span>, "llarg")}
          {camp("Referència", false, <span className="dv2-value">{refProducte || "—"}</span>)}
          {camp("Estoc físic", false, <span className="dv2-value">{formatNumero(estocFisic)}</span>, "curt")}
          {camp("Reservat", false, <span className="dv2-value">{formatNumero(estocReservat)}</span>, "curt")}
          {camp(
            "Disponible",
            false,
            <span className="dv2-value">{formatNumero((Number(estocFisic) || 0) - (Number(estocReservat) || 0))}</span>,
            "curt"
          )}
          {camp("Unitat de mesura", false, <span className="dv2-value">{unitatMesura}</span>)}
          {camp("Qualitat", false, <span className="dv2-value">{mapToPresentable(qualitat, mapejos.qualitat)}</span>)}
          {camp("És un pack", false, <span className={`dv2-chip ${espack ? "dv2-chip--ok" : ""}`}>{espack ? "Sí" : "No"}</span>, "curt")}
          {Number(product?.PackComposicioActiva) === 1 &&
            camp("Ubicació", false, <span className="dv2-value">{product?.UbicacioPack || "—"}</span>)}
          {camp("Descripció", false, <span className="dv2-value">{descripcio || "—"}</span>, "ample")}
          {Number(product?.PackComposicioActiva) === 1 && (product?.componentsPack || []).length > 0 && (
            <div className="dv2-field dv2-field--ample">
              <span className="dv2-label">Components</span>
              {(product.componentsPack || []).map((component) => (
                <span key={component.idProducte} className="dv2-value">
                  {formatNumero(component.quantitat)} × {[component.marca, component.model].filter(Boolean).join(" ")}
                </span>
              ))}
              {product.EstocPareIncidencia ? (
                <span className="dv2-hint">El pack té estoc residual al producte pare i no se suma.</span>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <PanellEstocProducte
        product={product}
        idEmpresa={empresaSeleccionada?.IdEmpresa || null}
        potTraslladar={pucModificar && mode === "consultar"}
        onEstoc={(fisic, reservat) => {
          setEstocFisic(fisic ?? 0);
          setEstocReservat(reservat ?? 0);
        }}
      />

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">4</span>
          <h2 className="dv2-card-title">Imatge</h2>
        </div>
        {imatge ? (
          <img src={imatge} alt="Imatge del producte" className="dv2-imagePreview" />
        ) : (
          <span className="dv2-value">—</span>
        )}
      </section>
    </>
  );

  // =================== EDITABLE ===================
  const renderEditable = () => {
    const categoriaNom =
      (categoriesOptions || []).find((x) => Number(x.idCategories) === Number(selectedCategoriaId))?.Categoria ||
      materialInfo?.Categoria ||
      "";
    const tipusNom =
      (tipusOptions || []).find((x) => Number(x.idTipusDeMaterials) === Number(selectedTipusId))?.Tipus ||
      materialInfo?.Tipus ||
      "";
    const materialNom =
      (materialsList || []).find((m) => Number(m.idMaterials) === Number(idMaterials))?.NomGeneric ||
      materialInfo?.NomGeneric ||
      "";

    return (
    <>
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Classificació</h2>
        </div>
        <p className="dv2-hint">
          Tria Categoria, Tipus i Material. Si encara no existeixen, crea'ls aquí sense sortir del producte.
        </p>
        <p className="dv2-path">
          <strong>{categoriaNom || "Categoria"}</strong>
          <span> › </span>
          <strong>{tipusNom || "Tipus"}</strong>
          <span> › </span>
          <strong>{materialNom || "Material"}</strong>
        </p>
        <div className="dv2-fields dv2-fields--sizes">
          {camp(
            "Categoria",
            false,
            <div className="dv2-controlRow">
              <select className="dv2-select" value={selectedCategoriaId ?? ""} onChange={(e) => handleChangeCategoriaId(e.target.value)}>
                <option value="">-- Selecciona Categoria --</option>
                {(categoriesOptions || []).map((c) => (
                  <option key={c.idCategories} value={c.idCategories}>
                    {c.Categoria}
                  </option>
                ))}
              </select>
              {pucCrear && (
                <button type="button" className="dv2-btn dv2-btn--compact" onClick={() => openModal("categoria")}>
                  + Nova
                </button>
              )}
            </div>,
            "llarg"
          )}
          {camp(
            "Tipus de material",
            false,
            <div className="dv2-controlRow">
              <select
                className="dv2-select"
                value={selectedTipusId ?? ""}
                onChange={(e) => handleChangeTipusId(e.target.value)}
                disabled={!selectedCategoriaId}
                title={!selectedCategoriaId ? "Selecciona una categoria primer" : ""}
              >
                <option value="">-- Selecciona Tipus --</option>
                {tipusList.map((t) => (
                  <option key={t.idTipusDeMaterials} value={t.idTipusDeMaterials}>
                    {t.Tipus}
                  </option>
                ))}
              </select>
              {pucCrear && (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--compact"
                  onClick={() => handleChangeTipusId("__new__")}
                  disabled={!selectedCategoriaId}
                >
                  + Nou
                </button>
              )}
            </div>,
            "llarg"
          )}
          {camp(
            "Material",
            true,
            <div className="dv2-controlRow">
              <select
                className="dv2-select"
                value={idMaterials ?? ""}
                onChange={(e) => handleChangeMaterialId(e.target.value)}
                disabled={!selectedTipusId}
                title={!selectedTipusId ? "Selecciona un tipus primer" : ""}
              >
                <option value="">-- Selecciona Material --</option>
                {materialsList.map((m) => (
                  <option key={m.idMaterials} value={m.idMaterials}>
                    {m.NomGeneric}
                  </option>
                ))}
              </select>
              {pucCrear && (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--compact"
                  onClick={() => handleChangeMaterialId("__new__")}
                  disabled={!selectedTipusId}
                >
                  + Nou
                </button>
              )}
            </div>,
            "llarg"
          )}
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Dades del producte</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          {camp("Marca", true, <input type="text" className="dv2-input" value={marca} onChange={(e) => setMarca(e.target.value)} />, "llarg")}
          {camp("Model", true, <input type="text" className="dv2-input" value={model} onChange={(e) => setModel(e.target.value)} />, "llarg")}
          {camp("Referència", false, <input type="text" className="dv2-input" value={refProducte} onChange={(e) => setRefProducte(e.target.value)} />)}
          {Number(product?.PackComposicioActiva) === 1
            ? camp("Estoc físic", false, <span className="dv2-value">{formatNumero(estocFisic)}</span>, "curt")
            : camp(
                "Estoc físic",
                true,
                <input type="number" className="dv2-input" value={estocFisic} onChange={(e) => setEstocFisic(e.target.value)} />,
                "curt"
              )}
          {camp("Reservat", false, <span className="dv2-value">{formatNumero(estocReservat)}</span>, "curt")}
          {camp(
            "Disponible",
            false,
            <span className="dv2-value">{formatNumero((Number(estocFisic) || 0) - (Number(estocReservat) || 0))}</span>,
            "curt"
          )}
          {camp(
            "Unitat de mesura",
            true,
            <select className="dv2-select" value={unitatMesura} onChange={(e) => setUnitatMesura(e.target.value)}>
              {unitatOptions.map((um) => (
                <option key={um} value={um}>
                  {um}
                </option>
              ))}
            </select>
          )}
          {camp(
            "Qualitat",
            true,
            <select className="dv2-select" value={qualitat} onChange={(e) => setQualitat(e.target.value)}>
              {qualitatOptions.map((q) => (
                <option key={q} value={q}>
                  {mapToPresentable(q, mapejos.qualitat)}
                </option>
              ))}
            </select>
          )}
          {camp(
            "És un pack",
            false,
            <span className="dv2-toggleRow">
              <span className="dv2-toggle">
                <input type="checkbox" checked={!!espack} onChange={(e) => setEsPack(e.target.checked ? 1 : 0)} />
                <span className="dv2-toggle-track" />
              </span>
              {espack ? "Sí" : "No"}
            </span>,
            "curt"
          )}
          {camp(
            "Descripció",
            false,
            <input type="text" className="dv2-input" value={descripcio} onChange={(e) => setDescripcio(e.target.value)} />,
            "ample"
          )}
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">3</span>
          <h2 className="dv2-card-title">Imatge</h2>
        </div>
        {imatge ? <img src={imatge} alt="Imatge del producte" className="dv2-imagePreview" /> : null}
        <input type="file" accept="image/*" className="dv2-input" onChange={(e) => handleImageUpload(e)} ref={fileInputRef} />
      </section>
    </>
    );
  };

  const potNavegar = ["consultar", "modificar"].includes(mode) && productes && productes.length > 1 && currentIndex >= 0;

  const titolFitxa =
    mode === "nou"
      ? "Nou producte"
      : mode === "esborrar"
      ? "Esborrar producte"
      : mode === "modificar"
      ? "Modificar producte"
      : "Productes · Fitxa";

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaCataleg pestanya="producteFitxa" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "📦"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolFitxa}</h1>
            <p className="dv2-pageHeader-subtitle">
              {mode === "nou"
                ? "Classifica el producte i omple marca i model. Pots crear categoria, tipus o material sense sortir."
                : mode === "esborrar"
                ? "No es podrà esborrar si té proveïdors associats."
                : [marca, model].filter(Boolean).join(" ") || "Dades del producte"}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funciona la fitxa del Producte" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && pucEsborrarFitxa && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar producte
            </button>
          )}
        </div>
      </div>
      {subnav}

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si el producte té proveïdors associats, el servidor no el deixarà esborrar.
        </div>
      )}

      <div className="dv2-entity-body">{isReadOnly ? renderReadOnly() : renderEditable()}</div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anteriorProducte} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {currentIndex + 1} / {productes.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={seguentProducte} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar producte
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar producte" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {renderModal()}
    </form>
  );
}

export default Detallsproducte;
